import {
  GridAggregationFunction,
  GridAggregationGetCellValueParams,
  GridColumnGroupingModel,
  GridRenderCellParams,
  GridTreeDataGroupingCell,
} from '@mui/x-data-grid-premium';
import { isValid } from 'date-fns';

export const groupingColDef = {
  headerName: 'Results',
  hideDescendantCount: true,
  minWidth: 200,
  renderCell: (params: GridRenderCellParams) => {
    if (params.id === 'auto-generated-group-footer-root') {
      return 'Totals';
    }

    if (isValid(new Date(params.value))) {
      return (
        <GridTreeDataGroupingCell hideDescendantCount {...(params as any)} />
      );
    }

    // hide id field in tree node
    if (params.value === undefined) return '';

    return (
      <GridTreeDataGroupingCell hideDescendantCount {...(params as any)} />
    );
  },
};

export const columnGroupingModel: GridColumnGroupingModel = [
  {
    groupId: 'field_data',
    headerName: 'Field',
    description: '',
    headerAlign: 'center',
    children: [
      { field: 'activeResourcesField' },
      { field: 'openSlotsFieldResource' },
      { field: 'additionalResourcesField' },
      { field: 'additionalResourcesFieldByDate' },
    ],
  },
  {
    groupId: 'virtual_data',
    headerName: 'Virtual',
    description: '',
    headerAlign: 'center',
    children: [
      { field: 'activeResourcesVirtual' },
      { field: 'openSlotsVirtualResource' },
      { field: 'additionalResourcesVirtual' },
      { field: 'additionalResourcesVirtualByDate' },
    ],
  },
  {
    groupId: 'claim_owner_data',
    headerName: 'Claim Owner',
    description: '',
    headerAlign: 'center',
    children: [
      { field: 'activeResourcesClaimOwner' },
      { field: 'openSlotsClaimOwnerResource' },
      { field: 'additionalResourcesClaimOwner' },
      { field: 'additionalResourcesClaimOwnerByDate' },
    ],
  },
];

export const activeResourcesFieldSum: GridAggregationFunction<
  {
    activeResourcesFieldByDate: string;
    activeResourcesFieldByLocation: string;
  },
  number
> = {
  label: '',
  getCellValue: ({ row }: GridAggregationGetCellValueParams) => {
    return {
      activeResourcesFieldByDate: row?.['activeResources']?.['fieldByDate'],
      activeResourcesFieldByLocation:
        row?.['activeResources']?.['fieldByLocation'],
    };
  },
  apply: ({ values, ...rest }) => {
    if (String(rest.groupId).includes('teamName')) return;

    if (String(rest.groupId).includes('location')) {
      return Number(values[0]?.activeResourcesFieldByLocation);
    }
    if (rest.groupId === 'auto-generated-group-node-root') {
      return null;
    }

    return Number(values[0]?.activeResourcesFieldByDate);
  },
  columnTypes: ['number'],
};

export const activeResourcesClaimOwnerSum: GridAggregationFunction<
  {
    activeResourcesClaimOwnerByDate: string;
    activeResourcesClaimOwnerByLocation: string;
  },
  number
> = {
  label: '',
  getCellValue: ({ row }: GridAggregationGetCellValueParams) => {
    return {
      activeResourcesClaimOwnerByDate:
        row?.['activeResources']?.['claimOwnerByDate'],
      activeResourcesClaimOwnerByLocation:
        row?.['activeResources']?.['claimOwnerByLocation'],
    };
  },
  apply: ({ values, ...rest }) => {
    if (String(rest.groupId).includes('teamName')) return;

    if (String(rest.groupId).includes('location')) {
      return Number(values[0]?.activeResourcesClaimOwnerByLocation);
    }
    if (rest.groupId === 'auto-generated-group-node-root') {
      return null;
    }

    return Number(values[0]?.activeResourcesClaimOwnerByDate);
  },
  columnTypes: ['number'],
};

export const activeResourcesVirtualSum: GridAggregationFunction<
  {
    activeResourcesVirtualByDate: string;
    activeResourcesVirtualByLocation: string;
  },
  number
> = {
  label: '',
  getCellValue: ({ row }: GridAggregationGetCellValueParams) => {
    return {
      activeResourcesVirtualByDate: row?.['activeResources']?.['virtualByDate'],
      activeResourcesVirtualByLocation:
        row?.['activeResources']?.['virtualByLocation'],
    };
  },
  apply: ({ values, ...rest }) => {
    if (String(rest.groupId).includes('teamName')) return;

    if (String(rest.groupId).includes('location')) {
      return Number(values[0]?.activeResourcesVirtualByLocation);
    }
    if (rest.groupId === 'auto-generated-group-node-root') {
      return null;
    }

    return Number(values[0]?.activeResourcesVirtualByDate);
  },
  columnTypes: ['number'],
};

export const additionalResourcesSum: GridAggregationFunction<
  {
    additionalResourcesFieldByDate: string;
    additionalResourcesFieldByLocation: string;
  },
  number
> = {
  label: '',
  getCellValue: ({ row }: GridAggregationGetCellValueParams) => {
    return {
      additionalResourcesFieldByDate:
        row?.['additionalResources']?.['fieldByDate'],
      additionalResourcesFieldByLocation:
        row?.['additionalResources']?.['fieldByLocation'],
    };
  },
  apply: ({ values, ...rest }) => {
    if (String(rest.groupId).includes('teamName')) return;

    if (rest.groupId === 'auto-generated-group-node-root') {
      return null;
    }

    // we only show additional resource count per date, and in the footer
    if (String(rest.groupId).includes('location')) {
      return Number(values[0]?.additionalResourcesFieldByLocation);
    }

    return Number(values[0]?.additionalResourcesFieldByDate);
  },
  columnTypes: ['number'],
};

export const additionalResourcesClaimOwnerSum: GridAggregationFunction<
  {
    additionalResourcesClaimOwnerByDate: string;
    additionalResourcesClaimOwnerByLocation: string;
  },
  number
> = {
  label: '',
  getCellValue: ({ row }: GridAggregationGetCellValueParams) => {
    return {
      additionalResourcesClaimOwnerByDate:
        row?.['additionalResources']?.['claimOwnerByDate'],
      additionalResourcesClaimOwnerByLocation:
        row?.['additionalResources']?.['claimOwnerByLocation'],
    };
  },
  apply: ({ values, ...rest }) => {
    if (String(rest.groupId).includes('teamName')) return;

    if (rest.groupId === 'auto-generated-group-node-root') {
      return null;
    }

    // we only show additional resource count per date, and in the footer
    if (String(rest.groupId).includes('location')) {
      return Number(values[0]?.additionalResourcesClaimOwnerByLocation);
    }

    return Number(values[0]?.additionalResourcesClaimOwnerByDate);
  },
  columnTypes: ['number'],
};

export const additionalResourcesVirtualSum: GridAggregationFunction<
  {
    additionalResourcesVirtualByDate: string;
    additionalResourcesVirtualByLocation: string;
  },
  number
> = {
  label: '',
  getCellValue: ({ row }: GridAggregationGetCellValueParams) => {
    return {
      additionalResourcesVirtualByDate:
        row?.['additionalResources']?.['virtualByDate'],
      additionalResourcesVirtualByLocation:
        row?.['additionalResources']?.['virtualByLocation'],
    };
  },
  apply: ({ values, ...rest }) => {
    if (String(rest.groupId).includes('teamName')) return;

    if (rest.groupId === 'auto-generated-group-node-root') {
      return null;
    }

    // we only show additional resource count per date, and in the footer
    if (String(rest.groupId).includes('location')) {
      return Number(values[0]?.additionalResourcesVirtualByLocation);
    }

    return Number(values[0]?.additionalResourcesVirtualByDate);
  },
  columnTypes: ['number'],
};

export const openSlotsSum: GridAggregationFunction<
  {
    totalFieldData: { openSlotsCount: number };
    openSlotsFieldByDate: string;
    openSlotsFieldByLocation: string;
    openSlotsFieldResource: string;
  },
  number
> = {
  label: '',
  getCellValue: ({ row }: GridAggregationGetCellValueParams) => {
    return {
      totalFieldData: row?.['totalFieldOpenSlots'],
      openSlotsFieldByDate: row?.['openSlots']?.['fieldByDate'],
      openSlotsFieldByLocation: row?.['openSlots']?.['fieldByLocation'],
      openSlotsFieldResource: row?.['resourceOpenSlots']?.['field'],
    };
  },
  apply: ({ values, ...rest }) => {
    if (String(rest.groupId).includes('teamName')) return;
    if (String(rest.groupId).includes('location')) {
      return Number(values[0]?.openSlotsFieldByLocation);
    }

    if (rest.groupId === 'auto-generated-group-node-root') {
      return values.length > 0 ? Number(values[0]?.totalFieldData) : 0;
    }

    return Number(values[0]?.openSlotsFieldByDate);
  },
  columnTypes: ['number'],
};

export const openSlotsSumClaimOwner: GridAggregationFunction<
  {
    totalClaimOwnerData: { openSlotsCount: number };

    openSlotsClaimOwnerByDate: string;
    openSlotsClaimOwnerByLocation: string;
    openSlotsClaimOwnerResource: string;
  },
  number
> = {
  label: '',
  getCellValue: ({ row }: GridAggregationGetCellValueParams) => {
    return {
      totalClaimOwnerData: row?.['totalClaimOwnerOpenSlots'],
      openSlotsClaimOwnerByDate: row?.['openSlots']?.['claimOwnerByDate'],
      openSlotsClaimOwnerByLocation:
        row?.['openSlots']?.['claimOwnerByLocation'],
      openSlotsClaimOwnerResource: row?.['resourceOpenSlots']?.['claimOwner'],
    };
  },
  apply: ({ values, ...rest }) => {
    if (String(rest.groupId).includes('teamName')) return;

    if (String(rest.groupId).includes('location')) {
      return Number(values[0]?.openSlotsClaimOwnerByLocation);
    }

    if (rest.groupId === 'auto-generated-group-node-root') {
      return values.length > 0 ? Number(values[0]?.totalClaimOwnerData) : 0;
    }

    return Number(values[0]?.openSlotsClaimOwnerByDate);
  },
  columnTypes: ['number'],
};

export const openSlotsSumVirtual: GridAggregationFunction<
  {
    totalVirtualData: { openSlotsCount: number };

    openSlotsVirtualByDate: string;
    openSlotsVirtualByLocation: string;
  },
  number
> = {
  label: '',
  getCellValue: ({ row }: GridAggregationGetCellValueParams) => {
    return {
      totalVirtualData: row?.['totalVirtualOpenSlots'],
      openSlotsVirtualByDate: row?.['openSlots']?.['virtualByDate'],
      openSlotsVirtualByLocation: row?.['openSlots']?.['virtualByLocation'],
    };
  },
  apply: ({ values, ...rest }) => {
    if (String(rest.groupId).includes('teamName')) return;

    if (String(rest.groupId).includes('location')) {
      return Number(values[0]?.openSlotsVirtualByLocation);
    }

    if (rest.groupId === 'auto-generated-group-node-root') {
      return values.length > 0 ? Number(values[0]?.totalVirtualData) : 0;
    }

    return Number(values[0]?.openSlotsVirtualByDate);
  },
  columnTypes: ['number'],
};
